import type { OnboardingConfig, OnboardingState, OnboardingStepConfig } from '~/types/onboarding';

interface UseOnboardingStateOptions {
  initialState?: Partial<OnboardingState>;
  initialStepId?: string;
  /** Produces a blank state for resets; defaults to the standard onboarding shape. */
  createEmptyState?: () => OnboardingState;
}

export const useOnboardingState = (
  config: OnboardingConfig,
  options: UseOnboardingStateOptions = {},
) => {
  const createEmptyState = options.createEmptyState ?? (() => ({ email: '', otpCode: '', agreedToTerms: false, isNewUserRole: false, canBeMarketed: false, tooYoung: false }));

  const state = ref<OnboardingState>({
    ...createEmptyState(),
    ...options.initialState,
  });

  const findStepIndex = (stepId: string) =>
    config.steps.findIndex(s => s.id === stepId);

  const initialIndex = options.initialStepId !== undefined
    ? findStepIndex(options.initialStepId)
    : 0;
  const currentStepIndex = ref(initialIndex >= 0 ? initialIndex : 0);

  const loading = ref(false);
  const finalStepComplete = ref(false);

  const currentStep = computed((): OnboardingStepConfig => config.steps[currentStepIndex.value]!);
  const isLastStep = computed(
    () => currentStepIndex.value === config.steps.length - 1,
  );

  // Walk forward past any steps whose skipIf predicate matches the current state.
  const nextStepIndex = (from: number) => {
    let next = from + 1;
    while (next < config.steps.length && config.steps[next]!.skipIf?.(state.value)) {
      next++;
    }
    return next;
  };

  const advance = async () => {
    // Reaching the last step IS the completion — mark it before awaiting the side effect so
    // observers (e.g. analytics) fire before any redirect inside onComplete tears the page down.
    const reachedFinalStep = isLastStep.value;
    if (reachedFinalStep) {
      finalStepComplete.value = true;
    }

    await currentStep.value.onComplete?.(state.value);

    if (reachedFinalStep) {
      return;
    }

    const next = nextStepIndex(currentStepIndex.value);
    if (next >= config.steps.length) {
      finalStepComplete.value = true;
      return;
    }
    currentStepIndex.value = next;
  };

  const goToStep = (stepId: string) => {
    const idx = findStepIndex(stepId);
    if (idx >= 0) {
      currentStepIndex.value = idx;
    }
  };

  const resetTo = (stepId: string) => {
    state.value = createEmptyState();
    finalStepComplete.value = false;
    goToStep(stepId);
  };

  return {
    state,
    currentStep,
    advance,
    resetTo,
    loading,
    finalStepComplete,
  };
};
